import React from 'react';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  fullWidth?: boolean;
  options: { value: string; label: string }[];
}

/**
 * Select component with label, error state, and accessibility features
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, fullWidth = false, options, className, id, ...props }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const hintId = `${selectId}-hint`;

    const baseClasses =
      'w-full px-4 py-3 border rounded-xl font-medium bg-white/95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:shadow-[0_0_0_6px_rgba(37,99,235,0.08)]';

    const errorClasses = error
      ? 'border-red-500 bg-red-50 text-red-900'
      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400';

    const combinedClass = [baseClasses, errorClasses, fullWidth && 'w-full', className]
      .filter(Boolean)
      .join(' ');

    const ariaDescribedBy = [error && errorId, hint && hintId].filter(Boolean).join(' ');

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-2 select-none"
          >
            {label}
          </label>
        )}

        <select
          ref={ref}
          id={selectId}
          className={combinedClass}
          aria-invalid={!!error}
          aria-describedby={ariaDescribedBy || undefined}
          {...props}
        >
          <option value="">Select an option</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {error && (
          <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="mt-1 text-sm text-gray-500">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
